import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { useLang } from "../i18n";

export default function Header() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const navItems = [
    { to: "/", label: t.nav.home, testid: "nav-home" },
    { to: "/sell", label: t.nav.sell, testid: "nav-sell" },
    { to: "/buy", label: t.nav.buy, testid: "nav-buy" },
    { to: "/rent", label: t.nav.rent, testid: "nav-rent" },
    { to: "/about", label: t.nav.about, testid: "nav-about" },
    { to: "/contact", label: t.nav.contact, testid: "nav-contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#0F0F0F]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between h-20 md:h-24">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3 md:gap-4 min-w-0">
          <div className="flex flex-col leading-none shrink-0">
            <span className="font-serif text-[#D4AF37] text-xl sm:text-2xl md:text-3xl tracking-wide whitespace-nowrap">Roland Azar</span>
            <span className="font-serif italic text-white/85 text-[0.7rem] sm:text-xs md:text-sm tracking-wide whitespace-nowrap mt-0.5">L'Expert immobilier P.M.</span>
            <span className="block w-full h-px bg-[#D4AF37] my-1.5"></span>
            <span className="text-[0.55rem] sm:text-[0.6rem] md:text-[0.65rem] text-white uppercase tracking-[0.2em] whitespace-nowrap hidden sm:inline">Courtier immobilier, résidentiel et commercial</span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7 xl:gap-10">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `link-underline text-[0.72rem] uppercase tracking-[0.25em] transition-colors ${
                  isActive ? "text-[#D4AF37]" : "text-white/70 hover:text-[#D4AF37]"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          {/* Lang switcher */}
          <div className="flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.2em]" data-testid="lang-switcher">
            <button
              onClick={() => setLang("fr")}
              data-testid="lang-fr"
              className={`transition-colors ${lang === "fr" ? "text-[#D4AF37]" : "text-white/40 hover:text-white/80"}`}
            >FR</button>
            <span className="text-white/20">/</span>
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={`transition-colors ${lang === "en" ? "text-[#D4AF37]" : "text-white/40 hover:text-white/80"}`}
            >EN</button>
          </div>

          <Link to="/contact" className="hidden md:inline-block btn-gold" data-testid="header-cta">
            {t.cta.private_consultation}
          </Link>

          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={24} weight="thin" /> : <List size={24} weight="thin" />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {open && (
        <div className="lg:hidden bg-[#0F0F0F] border-t border-white/10" data-testid="mobile-nav">
          <nav className="flex flex-col px-6 py-8 gap-6">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`${n.testid}-mobile`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.25em] ${isActive ? "text-[#D4AF37]" : "text-white/70"}`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/contact" className="btn-gold mt-2 text-center" data-testid="header-cta-mobile">
              {t.cta.private_consultation}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
